/**
 * Everything /demo needs to run the real dashboard with no login and no
 * backend:
 *
 * - a fake auth session (Republic Plumbing / Sarah Mitchell by default, or
 *   whoever `?from=<invite token>` resolves to — see `./identity`) layered
 *   over the app's own AuthProvider, so `useAuth()` inside the dashboard sees
 *   a signed in user without a token ever existing;
 * - the fixture adapter from `./demoApi`, installed during render (before
 *   AuthProvider's boot request can fire) and ejected on unmount, so real
 *   routes are untouched;
 * - the "changes are not saved" toast for every mutation the tour accepts;
 * - a link guard, because the dashboard's own links point at /dashboard/… and
 *   inside the tour they must stay under /demo.
 *
 * `?from=` resolution (fnd-48a):
 *   - a demo token (`demo-no-site`, `demo-weak-site`) resolves from
 *     `DEMO_INVITES` synchronously, during render — no network — so the
 *     identity is in place before `demoApi` answers its first request.
 *   - a real invite token resolves from `GET /api/invite/:token`, which is
 *     necessarily async. The lookup is kicked during render and the fixture
 *     adapter awaits it (`whenDemoIdentityReady`) before answering anything,
 *     so no query ever sees the default business by accident.
 *   - no `from` at all → the static default identity, unless the tour was
 *     already opened from an invite in this session (chrome links drop the
 *     query string) — then the resolved identity stays.
 */

import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../hooks/useAuth';
import { useToast } from '../components/ui';
import { installDemoApi, uninstallDemoApi } from './demoApi';
import { DEMO_SAVE_NOTICE, emitDemoNotice, onDemoNotice } from './demoNotice';
import {
  buildDemoUser,
  getDemoIdentity,
  getDemoSource,
  resolveDemoIdentityFrom,
  setDemoSource,
  identityFromInvite,
  resetDemoIdentity,
  setDemoIdentity,
  subscribeDemoIdentity,
} from './identity';
import { DEMO_INVITES, isDemoToken } from '../components/invite/kit';

/** Nothing to render — it only wires the notice bridge to the toast system. */
const DemoNoticeBridge: React.FC = () => {
  const { showToast } = useToast();

  useEffect(() => onDemoNotice(message => showToast(message, 'info', 3500)), [showToast]);

  return null;
};

const DemoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');

  // Installed during render, not in an effect: AuthProvider sits above this
  // route and fires `POST /api/auth/refresh-token` from its own effect. Effects
  // run child-first, but a render-time install is the only ordering that is
  // guaranteed to beat it — and the call is idempotent.
  useState(() => {
    installDemoApi();
    return null;
  });

  useEffect(() => {
    installDemoApi(); // StrictMode remount
    return () => uninstallDemoApi();
  }, []);

  /**
   * Resolve `?from=` into the current identity. A demo token resolves
   * synchronously — set it right here, during render, so `demoApi` never
   * answers even its very first request with the wrong business. A real
   * token can only resolve once the network call returns; that branch runs
   * as a render-time-kicked side effect and applies the result whenever it
   * lands, guarded so StrictMode's double-render doesn't double-fetch.
   */
  const resolvedFromRef = useRef<string | null>(null);
  if (from && resolvedFromRef.current !== from) {
    resolvedFromRef.current = from;
    setDemoSource(from);
    if (isDemoToken(from)) {
      setDemoIdentity(identityFromInvite(DEMO_INVITES[from]));
    } else {
      // Kicked here, during render, so the adapter's readiness gate is armed
      // before the dashboard's first query reaches it.
      resolveDemoIdentityFrom(from);
    }
  } else if (!from && resolvedFromRef.current === null && getDemoSource() === null) {
    // Plain /demo, never opened from an invite in this session: the static
    // default. A route inside the tour that merely lost `?from=` (the chrome
    // rebases /dashboard/* → /demo/* without the query string) keeps the
    // identity it already resolved.
    resolvedFromRef.current = null;
    resetDemoIdentity();
  }


  // Re-render whenever the resolved identity changes, so `auth.user` below
  // (read directly, not through react-query) picks up the new business/
  // contact without needing its own invalidation path.
  const identity = useSyncExternalStore(subscribeDemoIdentity, getDemoIdentity);

  // The dashboard chrome and its pages link to /dashboard/… — under the tour
  // those must resolve inside /demo instead of bouncing to the login page.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.('a');
      const href = anchor?.getAttribute('href');
      if (!href || !href.startsWith('/dashboard')) return;
      event.preventDefault();
      navigate(`/demo${href.slice('/dashboard'.length)}`);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [navigate]);

  const auth = useMemo(
    () => ({
      user: buildDemoUser(identity),
      loading: false,
      isAuthenticated: true,
      login: async () => {
        emitDemoNotice(DEMO_SAVE_NOTICE);
      },
      logout: async () => {
        emitDemoNotice('Demo mode — there is no account to log out of');
      },
      refreshAuth: async () => {},
      resendVerification: async () => true,
    }),
    [identity]
  );

  return (
    <AuthContext.Provider value={auth}>
      <DemoNoticeBridge />
      {children}
    </AuthContext.Provider>
  );
};

export default DemoProvider;
